import React from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Divider,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  HelpOutline as HelpIcon,
} from "@mui/icons-material";

interface ProjectionsHelpModalProps {
  open: boolean;
  onClose: () => void;
}

const ProjectionsHelpModal: React.FC<ProjectionsHelpModalProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
      BackdropProps={{
        sx: {
          backgroundColor: isDark ? "rgba(0, 0, 0, 0.8)" : "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 640,
          maxHeight: "90vh",
          overflow: "auto",
          backgroundColor: theme.palette.background.default,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            pb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `2px solid ${theme.palette.primary.main}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <HelpIcon
              sx={{ fontSize: 28, color: theme.palette.primary.main }}
            />
            <Typography
              variant="h5"
              component="h2"
              sx={{ fontWeight: 600, color: theme.palette.text.primary }}
            >
              Growth Projections Guide
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: isDark
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.05)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {/* Introduction */}
          <Section title="What is this tool?">
            <Typography
              sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}
            >
              This tool helps you answer a simple question:{" "}
              <strong>
                "If this company keeps growing, what might the share price be in
                the future?"
              </strong>
            </Typography>
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                lineHeight: 1.7,
                mt: 1.5,
              }}
            >
              You tell the tool how fast you think the company will grow, and it
              calculates what the share price could be over the next 5 years.
            </Typography>
          </Section>

          <Divider sx={{ my: 3, borderColor: theme.palette.divider }} />

          {/* How to Use - Step by Step */}
          <Section title="How to use it (step by step)">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <StepItem
                number={1}
                title="Start with the defaults"
                description="When you first load a company, the sliders are set to the company's current growth rate and profit margin. This shows you what happens if the company continues performing exactly as it has been."
              />
              <StepItem
                number={2}
                title="Adjust the Revenue Growth slider"
                description="Ask yourself: Do you think the company will grow faster or slower than it has recently? Slide right for faster growth, left for slower. Even small changes make a big difference over 5 years. Keep in mind that companies sometimes go through explosive growth periods that may not be sustainable. Analyst predictions can help you forecast more realistic future growth rates."
              />
              <StepItem
                number={3}
                title="Adjust the Net Income Margin slider"
                description="This is how much profit the company keeps from each dollar of sales. Companies that become more efficient over time might see this number increase. Leave it alone if you're unsure."
              />
              <StepItem
                number={4}
                title="Adjust the P/E Ratio slider"
                description="This controls how 'expensive' or 'cheap' you think the stock will be valued in the future. A higher P/E means investors are willing to pay a premium price for the stock."
              />
              <StepItem
                number={5}
                title="Look at the results"
                description="The table shows projected share prices for each year. The green 'Share Price CAGR' row shows your potential annual return if the stock reaches that price."
              />
            </Box>
          </Section>

          <Divider sx={{ my: 3, borderColor: theme.palette.divider }} />

          {/* What the Sliders Mean */}
          <Section title="What do the sliders mean?">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <SliderExplainer
                name="Revenue Growth"
                simple="How fast is the company's sales growing each year?"
                example="10% means if they made $100 this year, they'll make $110 next year"
              />
              <SliderExplainer
                name="Net Income Margin"
                simple="What percentage of sales becomes actual profit?"
                example="20% margin means for every $100 in sales, $20 is profit"
              />
              <SliderExplainer
                name="P/E Ratio"
                simple="How much are investors willing to pay for each dollar of profit?"
                example="A P/E of 25 means investors pay $25 for every $1 of annual profit"
              />
            </Box>
          </Section>

          <Divider sx={{ my: 3, borderColor: theme.palette.divider }} />

          {/* Fine-tuning Individual Years */}
          <Section title="Fine-tuning individual years">
            <Typography
              sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}
            >
              Click on any <strong>Rev Growth</strong> or{" "}
              <strong>Net Inc. Margins</strong> cell in the table to change that
              specific year. This is useful if you expect growth to slow down
              over time, or if you think margins will improve as the company
              scales.
            </Typography>
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                lineHeight: 1.7,
                mt: 1.5,
              }}
            >
              Changed cells are highlighted. Moving the sliders will reset your
              custom values.
            </Typography>
          </Section>

          <Divider sx={{ my: 3, borderColor: theme.palette.divider }} />

          {/* Using Analyst Estimates */}
          <Section title="Where to find growth estimates">
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                lineHeight: 1.7,
                mb: 2,
              }}
            >
              Not sure what growth rate to use? You can see what professional
              analysts are predicting by clicking the{" "}
              <strong>Analyst Estimates</strong> tab at the top of this page.
            </Typography>
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                lineHeight: 1.7,
                mb: 2,
              }}
            >
              The table shows analyst predictions for future revenue and EPS,
              with low, average, and high estimates for each year. Compare the
              revenue estimates between years to calculate expected growth.
            </Typography>
            <Box
              sx={{
                p: 2,
                backgroundColor: isDark
                  ? "rgba(34, 197, 94, 0.1)"
                  : "rgba(34, 197, 94, 0.08)",
                borderRadius: 2,
                border: `1px solid ${isDark ? "rgba(34, 197, 94, 0.3)" : "rgba(34, 197, 94, 0.2)"}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  color: theme.palette.text.primary,
                  lineHeight: 1.6,
                }}
              >
                <strong>Example:</strong> If analysts estimate revenue will grow
                from $100B to $115B, that's 15% growth. You can use this as your
                Revenue Growth setting.
              </Typography>
            </Box>
          </Section>

          <Divider sx={{ my: 3, borderColor: theme.palette.divider }} />

          {/* Understanding CAGR */}
          <Section title="What is Share Price CAGR?">
            <Typography
              sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}
            >
              CAGR stands for "Compound Annual Growth Rate". It shows your{" "}
              <strong>predicted</strong> average yearly return if the share
              price reaches the projected level.
            </Typography>
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                lineHeight: 1.7,
                mt: 1.5,
              }}
            >
              For example, a 15% CAGR over 5 years equals a predicted 100% total
              return on your investment—assuming your assumptions about growth,
              margins, and valuations play out as expected and market conditions
              remain constant.
            </Typography>
          </Section>

          <Divider sx={{ my: 3, borderColor: theme.palette.divider }} />

          {/* Disclaimer */}
          <Box
            sx={{
              p: 2,
              backgroundColor: theme.palette.background.paper,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: theme.palette.text.secondary,
                lineHeight: 1.6,
              }}
            >
              <strong>Important:</strong> These are hypothetical projections,
              not predictions. The future is uncertain and actual results may be
              very different. This tool is for exploring "what if" scenarios and
              learning—not financial advice. Always do your own research before
              investing.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

// Helper Components
interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, children }) => {
  const theme = useTheme();

  return (
    <Box>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          fontSize: "1rem",
          color: theme.palette.text.primary,
          mb: 1.5,
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
};

interface StepItemProps {
  number: number;
  title: string;
  description: string;
}

const StepItem: React.FC<StepItemProps> = ({ number, title, description }) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          backgroundColor: theme.palette.primary.main,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 600,
          fontSize: "0.875rem",
          flexShrink: 0,
        }}
      >
        {number}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "0.9375rem",
            color: theme.palette.text.primary,
            mb: 0.5,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: theme.palette.text.secondary,
            lineHeight: 1.6,
            fontSize: "0.875rem",
          }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

interface SliderExplainerProps {
  name: string;
  simple: string;
  example: string;
}

const SliderExplainer: React.FC<SliderExplainerProps> = ({
  name,
  simple,
  example,
}) => {
  const theme = useTheme();

  return (
    <Box>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "0.9375rem",
          color: theme.palette.primary.main,
          mb: 0.25,
        }}
      >
        {name}
      </Typography>
      <Typography
        sx={{
          color: theme.palette.text.secondary,
          lineHeight: 1.5,
          fontSize: "0.875rem",
        }}
      >
        {simple}
      </Typography>
      <Typography
        sx={{
          color: theme.palette.text.secondary,
          lineHeight: 1.5,
          fontSize: "0.8125rem",
          mt: 0.5,
          fontStyle: "italic",
        }}
      >
        {example}
      </Typography>
    </Box>
  );
};

export default ProjectionsHelpModal;
